/**
 * El catálogo de gestos.
 *
 * Un gesto es lo que un jugador ESTÁ HACIENDO mientras no camina: saludar, bailar,
 * reírse. Hasta ahora el estado sincronizado sabía dónde estás, hacia dónde miras y
 * sobre qué estás parado, pero nada decía qué estás haciendo — y eso es la mitad de lo
 * que hace que una sala llena se lea como gente y no como avatares quietos.
 *
 * **Va en el estado sincronizado y no en un `broadcast`**, y la diferencia importa: un
 * aviso suelto sólo lo reciben los que ya estaban dentro, así que quien entra a la
 * plazoleta mientras cuatro bailan los vería a los cuatro parados. El estado es lo
 * único que describe el mundo TAL COMO ESTÁ para quien acaba de llegar.
 *
 * Es un catálogo por la misma razón que las salas, los ítems y los clubes: agregar un
 * gesto tiene que ser una entrada de datos, no código repartido entre el servidor y
 * tres archivos del cliente. Lo que NO vive acá es cómo se anima cada uno — eso es
 * presentación y vive en el cliente, igual que el arte de una sala no vive en `ROOMS`.
 */
/**
 * Los gestos, por índice. **Append-only**: el índice es lo que viaja por el cable
 * como `uint8`, así que reordenar esta lista le cambia el gesto a todo el que esté
 * haciendo uno. Misma regla que `FACINGS` y `AVATAR_COLORS`.
 *
 * `ninguno` ocupa el cero a propósito, en vez de desplazar los índices con un `+1` al
 * serializar: el valor por defecto de un `uint8` es 0, así que el hueco cero tiene que
 * significar "no está haciendo nada" o un jugador recién creado estaría saludando.
 */
export const GESTURES = [
    "ninguno",
    "saludo",
    "baile",
    "risa",
    "sorpresa",
    "aplauso",
    "siesta",
];
/** El índice de `ninguno`: el valor por defecto del campo y el de "ya terminó". */
export const NO_GESTURE_INDEX = 0;
export const GESTURE_DEFS = {
    /*
     * La ausencia también es una entrada del catálogo.
     *
     * Podría ser un caso especial en cada sitio que lea el índice, pero entonces cada
     * uno tendría que acordarse de él. Como entrada, `GESTURE_DEFS[g]` nunca es
     * `undefined` y el compilador lo garantiza; lo que se filtra es el MENÚ, una vez,
     * en `GESTURE_LIST`.
     */
    ninguno: { id: "ninguno", name: "Nada", glyph: "", seconds: 0, command: "" },
    saludo: { id: "saludo", name: "Saludar", glyph: "👋", seconds: 3, command: "saludar" },
    baile: { id: "baile", name: "Bailar", glyph: "🎵", seconds: 60, command: "bailar" },
    risa: { id: "risa", name: "Reír", glyph: "😄", seconds: 3, command: "reir" },
    sorpresa: { id: "sorpresa", name: "Sorpresa", glyph: "❗", seconds: 2, command: "sorpresa" },
    aplauso: { id: "aplauso", name: "Aplaudir", glyph: "👏", seconds: 3, command: "aplaudir" },
    /*
     * El segundo que se sostiene, después de `baile`, y por eso dura decenas de segundos
     * y no tres: no es una reacción que se apague sola, es un estado en el que te quedas.
     * Menos que el baile igualmente, porque quedarse dormido es la broma y no el plan.
     */
    siesta: { id: "siesta", name: "Siesta", glyph: "😴", seconds: 30, command: "siesta" },
};
/** Los que se pueden elegir. `ninguno` no se pide: se llega a él terminando. */
export const GESTURE_LIST = GESTURES.filter((id) => id !== "ninguno").map((id) => GESTURE_DEFS[id]);
export function isGesture(value) {
    return typeof value === "string" && GESTURES.includes(value);
}
export function gestureFromIndex(index) {
    return GESTURES[index] ?? GESTURES[NO_GESTURE_INDEX];
}
/** El índice que viaja por el cable, o el de `ninguno` si no es un gesto. */
export function gestureIndex(value) {
    return isGesture(value) ? GESTURES.indexOf(value) : NO_GESTURE_INDEX;
}
/**
 * El gesto que pide una línea de chat, o `null` si no pide ninguno.
 *
 * Vive acá y no en el cliente para que el comando y la entrada del catálogo no puedan
 * separarse: el menú enseña `/bailar` porque el catálogo lo dice, y esto lo reconoce
 * leyendo lo mismo.
 *
 * Es un ATAJO de interfaz, no una regla: lo que llega al servidor sigue siendo
 * `Msg.Gesture` con un gesto que él valida contra el catálogo. Un cliente modificado
 * que mande el texto crudo sólo consigue decir "/bailar" en voz alta.
 */
/**
 * Los gestos que se podrían estar escribiendo, mientras se escriben.
 *
 * La barra sola los enseña todos; a partir de ahí filtra por prefijo. Vacío si el
 * texto no es un comando, así que quien lo llama no tiene que comprobarlo aparte.
 *
 * Vive junto a `gestureForCommand` y no en el cliente porque las dos tienen que leer
 * la MISMA regla de qué cuenta como comando —la barra, los espacios de más, las
 * mayúsculas—. Separadas, la tira acabaría sugiriendo algo que al pulsar Enter no se
 * ejecuta, que es peor que no sugerir nada.
 */
export function gesturesForPrefix(text) {
    if (!text.startsWith("/"))
        return [];
    const word = text.slice(1).trim().toLowerCase();
    return GESTURE_LIST.filter((gesture) => gesture.command.startsWith(word));
}
export function gestureForCommand(text) {
    if (!text.startsWith("/"))
        return null;
    const word = text.slice(1).trim().toLowerCase();
    const found = GESTURE_LIST.find((gesture) => gesture.command === word);
    return found ? found.id : null;
}
//# sourceMappingURL=gestures.js.map